import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Autocomplete,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  TextField,
  useTheme,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import TimeRangeToggle from './TimeRangeToggle';
import {
  useSESPerformanceTrends,
  type SESPerformanceTrendData,
} from '../../hooks/useSESPerformanceTrends';
import { useEngagementCampaigns } from '../../hooks/useEngagementCampaigns';
import {
  ALL_CAMPAIGNS_OPTION_ID,
  useEngagementCampaignFilter,
} from '../../contexts/EngagementCampaignFilterContext';
import {
  isSegmentTrackingEnabled,
  trackCampaignSelectionChanged,
} from '../../utils/segmentTracking';
import type { CampaignSelectionAnalyticsPayload } from '../../utils/segmentTracking';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';

const EMPTY_SUMMARY_SNAPSHOT: SESPerformanceTrendData = {
  date: '',
  sent: 0,
  delivered: 0,
  opened: 0,
  clicked: 0,
  bounced: 0,
  complained: 0,
  deliveryRate: 0,
  openRate: 0,
  clickRate: 0,
  bounceRate: 0,
  complaintRate: 0,
};

interface CampaignOption {
  id: string;
  label: string;
}

const CAMPAIGN_SELECTION_TRACKING_DEBOUNCE_MS = 400;
const CAMPAIGN_SELECTION_TRACKING_SOURCE = 'engagement-performance-trends';

const formatDateLabel = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const isServerOrClientError = (statusCode?: number): boolean => {
  if (!statusCode) {
    return false;
  }
  const group = Math.floor(statusCode / 100);
  return group === 4 || group === 5;
};

export interface EngagementPerformanceTrendsProps {
  timeRange: EngagementTimeRange;
  onTimeRangeChange: (range: EngagementTimeRange) => void;
  refreshToken?: number;
  onRefresh?: () => void | Promise<void>;
}

const EngagementPerformanceTrends: React.FC<EngagementPerformanceTrendsProps> = ({
  timeRange,
  onTimeRangeChange,
  refreshToken = 0,
  onRefresh,
}) => {
  const theme = useTheme();
  const { selectedCampaignId, setSelectedCampaignId, normalizedCampaignId } =
    useEngagementCampaignFilter();
  const emitCampaignSelectionAnalytics = useCampaignSelectionTracking();

  const {
    data: campaignsData,
    isLoading: areCampaignsLoading,
    isFetching: areCampaignsFetching,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useEngagementCampaigns();

  const campaigns = campaignsData ?? [];
  const isCampaignSelectorBusy = areCampaignsLoading || areCampaignsFetching;
  const {
    dataset: trendDataset,
    placeholderText: trendPlaceholder,
    isLoading,
    error,
    refetch,
    isFetching,
    apiError: trendApiError,
  } = useSESPerformanceTrends(timeRange, normalizedCampaignId);

  const refreshSignalRef = useRef(refreshToken);
  const skipNextRefreshRef = useRef(false);

  useEffect(() => {
    if (refreshSignalRef.current === refreshToken) {
      return;
    }

    refreshSignalRef.current = refreshToken;

    if (skipNextRefreshRef.current) {
      skipNextRefreshRef.current = false;
      return;
    }

    refetch();
  }, [refetch, refreshToken]);

  useEffect(() => {
    if (selectedCampaignId === ALL_CAMPAIGNS_OPTION_ID || !campaigns.length) {
      return;
    }
    const hasMatch = campaigns.some((campaign) => campaign.id === selectedCampaignId);
    if (!hasMatch) {
      setSelectedCampaignId(ALL_CAMPAIGNS_OPTION_ID);
    }
  }, [campaigns, selectedCampaignId]);

  const campaignOptions = useMemo<CampaignOption[]>(() => {
    const sortedCampaigns = [...campaigns].sort((a, b) => a.name.localeCompare(b.name));
    return [
      { id: ALL_CAMPAIGNS_OPTION_ID, label: 'All campaigns' },
      ...sortedCampaigns.map((campaign) => ({
        id: campaign.id,
        label: campaign.name || 'Untitled campaign',
      })),
    ];
  }, [campaigns]);

  const selectedCampaignOption = useMemo<CampaignOption>(() => {
    return (
      campaignOptions.find((option) => option.id === selectedCampaignId) ??
      campaignOptions[0]
    );
  }, [campaignOptions, selectedCampaignId]);

  const handleCampaignChange = (
    _event: React.SyntheticEvent,
    option: CampaignOption | null
  ) => {
    const nextCampaignId = option?.id ?? ALL_CAMPAIGNS_OPTION_ID;
    if (nextCampaignId === selectedCampaignId) {
      return;
    }

    setSelectedCampaignId(nextCampaignId);
    emitCampaignSelectionAnalytics(nextCampaignId, 'manual');
  };

  const handleResetCampaignSelection = () => {
    if (selectedCampaignId === ALL_CAMPAIGNS_OPTION_ID) {
      return;
    }

    setSelectedCampaignId(ALL_CAMPAIGNS_OPTION_ID);
    emitCampaignSelectionAnalytics(ALL_CAMPAIGNS_OPTION_ID, 'reset');
  };

  const chartData = useMemo(
    () =>
      trendDataset.map((entry) => ({
        date: formatDateLabel(entry.date),
        openRate: entry.openRate,
        clickRate: entry.clickRate,
        deliveryRate: entry.deliveryRate,
        bounceRate: entry.bounceRate,
      })),
    [trendDataset]
  );

  const latestSnapshot = useMemo(
    () => trendDataset[trendDataset.length - 1] ?? null,
    [trendDataset]
  );

  const hasTrendData = trendDataset.length > 0;
  const summarySnapshot = latestSnapshot ?? EMPTY_SUMMARY_SNAPSHOT;
  const shouldRenderSummary = hasTrendData || Boolean(trendPlaceholder);
  const summaryMetrics = useMemo(
    () => [
      {
        key: 'deliveryRate',
        label: 'Latest delivery rate',
        value: `${summarySnapshot.deliveryRate.toFixed(1)}%`,
      },
      {
        key: 'openRate',
        label: 'Latest open rate',
        value: `${summarySnapshot.openRate.toFixed(1)}%`,
      },
      {
        key: 'clickRate',
        label: 'Latest click rate',
        value: `${summarySnapshot.clickRate.toFixed(1)}%`,
      },
      {
        key: 'bounceRate',
        label: 'Latest bounce rate',
        value: `${summarySnapshot.bounceRate.toFixed(1)}%`,
      },
    ],
    [summarySnapshot]
  );

  const showScopedTrendWarning = Boolean(
    trendApiError &&
    normalizedCampaignId &&
    normalizedCampaignId !== ALL_CAMPAIGNS_OPTION_ID &&
    isServerOrClientError(trendApiError.statusCode)
  );

  const trendWarningMessage = trendApiError?.statusCode === 400
    ? 'This campaign selection could not be loaded. Please reset to view all campaigns.'
    : 'We could not load data for this campaign. Try resetting to all campaigns or refreshing later.';

  const handleRefreshClick = () => {
    skipNextRefreshRef.current = true;
    refetch();
    onRefresh?.();
  };

  return (
    <Box
      component="section"
      aria-label="Engagement performance trends"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          Engagement Performance Trends
        </Typography>
        <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }} sx={{ width: '100%' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
            useFlexGap
          >
            <Autocomplete
              size="small"
              options={campaignOptions}
              value={selectedCampaignOption}
              onChange={handleCampaignChange}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableClearable
              sx={{ minWidth: 220, flexGrow: 1 }}
              loading={isCampaignSelectorBusy}
              disabled={isCampaignSelectorBusy}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Campaign"
                  placeholder="All campaigns"
                  error={Boolean(campaignsError)}
                  helperText={
                    campaignsError ? 'Unable to refresh campaigns. Please retry.' : undefined
                  }
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': 'Select campaign for engagement trends',
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isCampaignSelectorBusy ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TimeRangeToggle value={timeRange} onChange={onTimeRangeChange} disabled={isLoading} />
            <Tooltip title="Refresh data">
              <span>
                <IconButton
                  onClick={handleRefreshClick}
                  disabled={isFetching}
                  aria-label="Refresh engagement trends"
                >
                  {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {showScopedTrendWarning ? (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                px: 1.5,
                py: 0.5,
                maxWidth: 360,
              }}
              role="alert"
              action={
                <Button
                  size="small"
                  onClick={handleResetCampaignSelection}
                  aria-label="Reset engagement trends to all campaigns"
                >
                  Reset
                </Button>
              }
            >
              <Typography variant="caption" color="warning.dark">
                {trendWarningMessage}
              </Typography>
            </Alert>
          ) : null}
        </Stack>
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        {campaignsError ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => refetchCampaigns()}
                disabled={isCampaignSelectorBusy}
              >
                Retry
              </Button>
            }
          >
            Unable to load your saved campaigns. Showing the last known list.
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error">Unable to load performance trends.</Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : trendPlaceholder ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {trendPlaceholder}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  stroke={theme.palette.text.secondary}
                />
                <RechartsTooltip formatter={(value: number) => `${value}%`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="openRate"
                  name="Open rate"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="clickRate"
                  name="Click rate"
                  stroke={theme.palette.success.main}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="deliveryRate"
                  name="Delivery rate"
                  stroke={theme.palette.warning.main}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="bounceRate"
                  name="Bounce rate"
                  stroke={theme.palette.error.main}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        {shouldRenderSummary ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={3}>
            {summaryMetrics.map((metric) => (
              <Box key={metric.key}>
                <Typography variant="caption" color="text.secondary">
                  {metric.label}
                </Typography>
                <Typography variant="h6">{metric.value}</Typography>
              </Box>
            ))}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
};

type CampaignSelectionType = CampaignSelectionAnalyticsPayload['selectionType'];

const useCampaignSelectionTracking = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (campaignId: string, selectionType: CampaignSelectionType = 'manual') => {
      if (!isSegmentTrackingEnabled()) {
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        trackCampaignSelectionChanged({
          campaignId,
          selectionType,
          triggeredAt: new Date().toISOString(),
          source: CAMPAIGN_SELECTION_TRACKING_SOURCE,
          isAllCampaigns: campaignId === ALL_CAMPAIGNS_OPTION_ID,
        });
      }, CAMPAIGN_SELECTION_TRACKING_DEBOUNCE_MS);
    },
    []
  );
};

export default memo(EngagementPerformanceTrends);
